import { writable, derived, get } from "svelte/store";
import axios from "../../utiles/axios.util";
import toast from "svelte-french-toast";

// ==========================================
// 1. 기준 연도 및 주차/구간 정보
// ==========================================
export const CURRENT_YEAR = new Date().getFullYear();

// 기준년도 선택 목록 (현재년도 기준 과거 3년 ~ 내년)
export const BASE_YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 3 + i);

// 년말확장 시 조회구간 뒤로 추가해서 보여줄 구간 수 (월: 3개월, 주: 13주, 분기: 1분기)
const EXTEND_MONTHS = 3;
const EXTEND_WEEKS = 13;
const EXTEND_QUARTERS = 1;

// 조회구간 기본값: 기준년도 1월 1일 ~ 12월 31일 (표시구간 1년 고정이 기본)
export const yearStartDate = (year) => `${year}-01-01`;
export const yearEndDate = (year) => `${year}-12-31`;

// 날짜 문자열(YYYY-MM-DD)을 기준년도 안으로 보정해서 { m, d } 반환
function clampToYear(dateStr, year, isStart) {
    const [y, m, d] = String(dateStr || "").split("-").map(Number);
    if (!y || !m || !d) return isStart ? { m: 1, d: 1 } : { m: 12, d: 31 };
    if (y < year) return { m: 1, d: 1 };
    if (y > year) return { m: 12, d: 31 };
    return { m, d };
}

// ISO 주차 계산 (DB WEEK_NO 의 연주차와 동일한 규칙)
function isoWeekOf(y, m, d) {
    const date = new Date(Date.UTC(y, m - 1, d));
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return { year: date.getUTCFullYear(), week: Math.ceil(((date - yearStart) / 86400000 + 1) / 7) };
}

// 구간 컬럼 정의 생성 (월 / 주 / 분기)
//  - year: 해당 컬럼의 연도, idx: 월/주/분기 번호
//  - 조회구간(시작일~종료일)이 포함된 구간만 표시, 년말확장은 조회구간 끝 뒤로 +3개월(13주/1분기)
function buildPeriodCols(periodType, baseYear, hasWeek53, startDate, endDate) {
    let s = clampToYear(startDate, baseYear, true);
    let e = clampToYear(endDate, baseYear, false);
    if (s.m * 100 + s.d > e.m * 100 + e.d) e = s;   // 시작이 종료보다 늦으면 종료를 시작에 맞춤

    const unit = periodType === "MONTH" ? "월" : periodType === "WEEK" ? "주" : "분기";
    const base = [];
    const ext = [];
    let first, last, total, extCount;

    if (periodType === "MONTH") {
        first = s.m; last = e.m; total = 12; extCount = EXTEND_MONTHS;
    } else if (periodType === "QUARTER") {
        first = Math.ceil(s.m / 3); last = Math.ceil(e.m / 3); total = 4; extCount = EXTEND_QUARTERS;
    } else {
        total = hasWeek53 ? 53 : 52;
        extCount = EXTEND_WEEKS;
        const sw = isoWeekOf(baseYear, s.m, s.d);
        const ew = isoWeekOf(baseYear, e.m, e.d);
        first = sw.year === baseYear ? sw.week : sw.year < baseYear ? 1 : total;
        last = ew.year === baseYear ? ew.week : ew.year > baseYear ? total : 1;
        first = Math.min(first, total);
        last = Math.min(Math.max(last, first), total);
    }

    const prefix = periodType === "MONTH" ? "M" : periodType === "WEEK" ? "W" : "Q";
    for (let i = first; i <= last; i++) {
        base.push({ key: `${prefix}${i}`, label: `${i}${unit}`, year: baseYear, idx: i, months: [i] });
    }

    // 년말확장: 조회구간 끝 다음 구간부터 이어서 (연말을 넘으면 차년으로 넘어감)
    let prevYear = baseYear;
    for (let k = 1; k <= extCount; k++) {
        let idx = last + k;
        let year = baseYear;
        if (idx > total) {
            idx -= total;
            year = baseYear + 1;
        }
        const label = year !== prevYear
            ? `${String(year).slice(-2)}년${idx}${unit}`
            : `${idx}${unit}`;
        ext.push({ key: `X${prefix}${idx}`, label, year, idx, months: [idx] });
        prevYear = year;
    }

    ext.forEach((c, i) => {
        c.isExt = true;
        c.isExtFirst = i === 0;
        c.isExtLast = i === ext.length - 1;
    });
    return { base, ext };
}

// ==========================================
// 2. 조회 조건 Store
// ==========================================
export const salesSearch = writable({
    excludeZero: true,                 // 0 제외 (경영/실적/계획이 모두 없는 품번 제외)
    planWeek: "",                      // 편성주차 (예: 20261044_1, 목록 조회 후 최신값으로 설정)
    baseYear: String(CURRENT_YEAR),    // 기준년도
    startDate: yearStartDate(CURRENT_YEAR), // 조회구간 시작일 (기본: 기준년도 1월 1일)
    endDate: yearEndDate(CURRENT_YEAR),     // 조회구간 종료일 (기본: 기준년도 12월 31일)
    modelIntegration: "",              // 기종통합 (통합기종 코드, 빈값 = 전체)
    treeCheckedIds: [],                // 제품 트리에서 체크한 노드 ID (복원용, 빈 배열 = 전체)
    selectedItemGroups: [],            // 제품 트리: 하위 기종 전체가 선택된 품목그룹 코드
    selectedGroupModels: [],           // 제품 트리: 일부 기종만 선택된 "품목그룹코드|기종코드"
    itemCode: "",                      // 제품품번
    perfType: "SALES",                 // 실적유형: 'SHIPMENT' (출하, ACMSLT_TY=D) vs 'SALES' (판매, ACMSLT_TY=S)
    unitType: "AMOUNT",                // 집계단위: 'QTY' (대) vs 'AMOUNT' (금액)
    periodType: "MONTH",               // 구간단위: 'WEEK' (주) vs 'MONTH' (월) vs 'QUARTER' (분기)
    monthBaseType: "DAY",              // 월편성기준: 'WEEK' (주차) vs 'DAY' (일, 기본값)
    yearEndExtend: false,              // 년말확장: 조회구간 뒤로 차년 +3개월 표시
    isExpanded: true,
});

// 실제 그리드에 적용된 조회 조건 (조회 버튼 강조 및 기준년도/편성주차 반영용)
export const appliedSalesSearch = writable(get(salesSearch));

// ==========================================
// 3. 컬럼 및 항목 설정 Store ([컬럼 구성] 설정 모달)
// ==========================================
export const MASTER_SALES_COLUMNS = [
    { id: "productType", label: "제품유형", group: "제품", defaultVisible: false },
    { id: "itemGroup", label: "품목그룹", group: "제품", defaultVisible: true },
    { id: "integratedModel", label: "기종통합", group: "제품", defaultVisible: false },
    { id: "model", label: "기종", group: "제품", defaultVisible: false },
    { id: "itemName", label: "품명", group: "제품", defaultVisible: true },
    { id: "itemCode", label: "품번", group: "제품", defaultVisible: true },
];

// 항목(행) 정의: 기준년도에 따라 라벨이 바뀌므로 함수/derived로 제공
//  - prevActual: 전년 실적(A), mgmtPlan: 당해 경영(M), curPlan: 당해 계획(N)
export function buildMetricRows(baseYear) {
    const y = parseInt(baseYear, 10) || CURRENT_YEAR;
    return [
        { id: "prevActual", label: `${y - 1} 실적`, isRatio: false, iem: "A", defaultVisible: true },
        { id: "mgmtPlan", label: `${y} 경영`, isRatio: false, iem: "M", defaultVisible: true },
        { id: "curPlan", label: `${y} 계획`, isRatio: false, iem: "N", defaultVisible: true },
        { id: "prevRatio", label: "- 전년비", isRatio: true, defaultVisible: true },
        { id: "mgmtRatio", label: "- 경영비", isRatio: true, defaultVisible: true },
    ];
}
export const metricRowDefs = derived(appliedSalesSearch, ($a) => buildMetricRows($a.baseYear));

export const salesColumnConfig = writable({
    visibleColumnIds: ["itemGroup", "itemCode", "itemName"],
    visibleMetricIds: ["prevActual", "mgmtPlan", "curPlan", "prevRatio", "mgmtRatio"],
});

// ==========================================
// 4. 데이터 조회 (BSN_MNGMT_SELNG)
// ==========================================
export const isSalesLoading = writable(false);

// 품번 단위로 묶은 원본 데이터: [{ itemCode, itemName, ..., rows: [{ iem, y, m, wy, wm, wn, qy, amt }] }]
export const rawSalesRecords = writable([]);

// 편성주차 목록
export const salesPlanWeeks = writable([]);

// 기종통합(통합기종) 목록: [{ code, name }]
export const salesModelIntegrations = writable([]);

export async function fetchSalesModelIntegrations() {
    try {
        const res = await axios.get("bsnMngmt/modelIntegrations");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(r => ({
                code: String(r.refrn_code || r.mhrls_unity_code || "").trim(),
                name: String(r.refrn_nm || r.mhrls_unity_nm || "").trim(),
            })).filter(m => m.code);
            salesModelIntegrations.set(list);
            return list;
        }
    } catch (e) {
        console.error("기종통합 조회 오류:", e);
    }
    return [];
}

export async function fetchSalesPlanWeeks() {
    try {
        const res = await axios.get("bsnMngmtSelng/planWeeks");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data
                .map(r => String(r.plan_week_no || r.PLAN_WEEK_NO || "").trim())
                .filter(Boolean);
            salesPlanWeeks.set(list);

            // 선택된 편성주차가 없거나 목록에 없으면 최신 편성주차로 설정
            const cur = get(salesSearch).planWeek;
            if (list.length > 0 && !list.includes(cur)) {
                salesSearch.update(s => ({ ...s, planWeek: list[0] }));
                appliedSalesSearch.update(s => ({ ...s, planWeek: list[0] }));
            }
            return list;
        }
    } catch (e) {
        console.error("편성주차 조회 오류:", e);
    }
    return [];
}

const toInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : n;
};

// API 행(일 단위) → 품번별 묶음
// ISO 연주차(y년 w주)의 목요일이 속한 달 (WEEK_NO 가 년주차 6자리일 때 주차 기준 월 계산용)
function isoWeekThursdayMonth(y, w) {
    if (!y || !w) return 0;
    const jan4 = new Date(y, 0, 4);
    const thursday = new Date(jan4);
    thursday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + 7 * (w - 1) + 3);
    return thursday.getMonth() + 1;
}

function groupRowsByItem(rows) {
    const map = new Map();
    rows.forEach(r => {
        const code = String(r.item_code || "").trim();
        if (!code) return;

        let item = map.get(code);
        if (!item) {
            item = {
                itemCode: code,
                itemName: r.item_nm || "",
                itemGroup: r.item_group_nm || r.item_group_code || "",
                productType: r.mlsfc_nm || r.mlsfc_code || "",
                integratedModel: r.mhrls_unity_nm || r.mhrls_unity_code || "",
                integratedModelCode: String(r.mhrls_unity_code || "").trim(),
                itemGroupCode: String(r.item_group_code || "").trim(),
                model: r.mhrls_knd_code || "",
                rows: [],
            };
            map.set(code, item);
        }

        // WEEK_NO: YYYY + 월(MM) + 연주차(WW)  → 주차 기준 연/월/주차
        // (WEEK_NO 가 년주차 6자리(YYYYWW)로 오면 월은 그 주 목요일이 속한 달로 계산)
        const wno = String(r.week_no || "").trim();
        const wno6 = wno.length === 6;
        item.rows.push({
            iem: String(r.iem_code || "").trim(),
            y: toInt(r.year),
            m: toInt(r.month),
            wy: toInt(wno.slice(0, 4)) || toInt(r.year),
            wm: wno6 ? isoWeekThursdayMonth(toInt(wno.slice(0, 4)), toInt(wno.slice(4, 6))) : toInt(wno.slice(4, 6)) || toInt(r.month),
            wn: wno6 ? toInt(wno.slice(4, 6)) : toInt(wno.slice(6, 8)),
            qy: Number(r.week_qy) || 0,
            amt: Number(r.week_amount) || 0,
        });
    });
    return [...map.values()].sort((a, b) =>
        a.itemGroup.localeCompare(b.itemGroup) || a.itemCode.localeCompare(b.itemCode)
    );
}

// 실적유형 → BSN_MNGMT_SELNG.ACMSLT_TY 코드
export const ACMSLT_TY = { SHIPMENT: "D", SALES: "S" };

let fetchToken = 0;

export async function fetchSalesStatusData() {
    const token = ++fetchToken;
    isSalesLoading.set(true);
    try {
        const search = get(salesSearch);
        appliedSalesSearch.set({ ...search });

        const baseYear = toInt(search.baseYear) || CURRENT_YEAR;
        const res = await axios.get("bsnMngmtSelng/list", {
            params: {
                plan_week_no: search.planWeek || "",
                acmslt_ty: ACMSLT_TY[search.perfType] || "S",   // 실적유형 (D: 출하, S: 판매)
                start_year: baseYear - 1,   // 전년 실적 비교용
                end_year: baseYear + 1,     // 년말확장(차년) 표시용
            },
        });
        if (token !== fetchToken) return;

        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            rawSalesRecords.set(groupRowsByItem(res.data.data));
        } else {
            rawSalesRecords.set([]);
            toast.error((res && res.data && res.data.msg) || "매출현황 조회에 실패했습니다.");
        }
    } catch (err) {
        if (token !== fetchToken) return;
        console.error("매출현황 데이터 처리 오류:", err);
        rawSalesRecords.set([]);
        toast.error("매출현황 조회 중 오류가 발생했습니다.");
    } finally {
        if (token === fetchToken) isSalesLoading.set(false);
    }
}

// 년말확장 토글 (이미 조회된 데이터로 즉시 반영되므로 적용 조건에도 함께 반영)
export function toggleYearEndExtend() {
    const next = !get(salesSearch).yearEndExtend;
    salesSearch.update(s => ({ ...s, yearEndExtend: next }));
    appliedSalesSearch.update(s => ({ ...s, yearEndExtend: next }));
}

// ==========================================
// 5. 집계 및 화면 표시용 테이블 모델 (Derived Store)
// ==========================================
function ratioOf(cur, base) {
    const diff = cur - base;
    return {
        diff,
        ratio: base > 0 ? ((diff / base) * 100).toFixed(1) : cur > 0 ? "100.0" : "0.0",
    };
}

// 일 단위 행 → (항목구분|연도|구간번호) 버킷
function periodKeyOf(row, periodType, monthBaseType) {
    if (periodType === "WEEK") return { y: row.wy, i: row.wn };
    // 월편성기준: 주차(해당 월에 편성된 주차 기준) vs 일(실제 일자의 월 기준)
    const useWeek = monthBaseType === "WEEK";
    const y = useWeek ? row.wy : row.y;
    const m = useWeek ? row.wm : row.m;
    return { y, i: periodType === "MONTH" ? m : Math.ceil(m / 3) };
}

// 제품 컬럼 정렬 상태 (기본: 품번 오름차순, 헤더 클릭 시 해당 컬럼 오름 ↔ 내림 전환)
export const DEFAULT_SORT = { key: "itemCode", dir: "asc" };
export const salesSort = writable({ ...DEFAULT_SORT });

const SORT_COLLATOR = new Intl.Collator("ko", { numeric: true, sensitivity: "base" });

// 표는 [조회하기]로 적용된 조건(appliedSalesSearch)만 기준으로 계산한다.
// 조회 영역(salesSearch)을 바꿔도 조회하기를 누르기 전에는 표가 바뀌지 않는다.
export const salesGridData = derived(
    [rawSalesRecords, appliedSalesSearch, salesColumnConfig, salesSort],
    ([$records, $applied, $colConfig, $sort]) => {
        const $search = $applied;
        const baseYear = toInt($applied.baseYear) || CURRENT_YEAR;
        const periodType = $search.periodType;
        const isAmount = $search.unitType === "AMOUNT";

        // 53주차 존재 여부 (주 단위 조회 시 53주 컬럼 추가)
        const hasWeek53 = periodType === "WEEK" &&
            $records.some(it => it.rows.some(r => r.wn === 53 && (r.wy === baseYear || r.wy === baseYear - 1)));

        const { base, ext } = buildPeriodCols(periodType, baseYear, hasWeek53, $search.startDate, $search.endDate);
        const baseCols = base;
        const extCols = $search.yearEndExtend ? ext : [];
        const allCols = [...baseCols, ...extCols];

        if (!$records || $records.length === 0) {
            return { items: [], grandTotal: null, periodCols: baseCols, extCols };
        }

        // 1) 필터링 (품번 검색)
        let filtered = $records;

        // 기종통합 필터
        if ($search.modelIntegration) {
            filtered = filtered.filter(r => r.integratedModelCode === $search.modelIntegration);
        }

        // 제품 트리 필터 (품목그룹 전체 선택 또는 "품목그룹|기종" 쌍), 선택이 없으면 전체
        const selGroups = $search.selectedItemGroups || [];
        const selPairs = $search.selectedGroupModels || [];
        if (selGroups.length > 0 || selPairs.length > 0) {
            const grpSet = new Set(selGroups);
            const pairSet = new Set(selPairs);
            filtered = filtered.filter(r =>
                grpSet.has(r.itemGroupCode) || pairSet.has(`${r.itemGroupCode}|${r.model}`)
            );
        }

        if ($search.itemCode && $search.itemCode.trim() !== "") {
            const kw = $search.itemCode.trim().toLowerCase();
            filtered = filtered.filter(r =>
                (r.itemCode || "").toLowerCase().includes(kw) ||
                (r.itemName || "").toLowerCase().includes(kw) ||
                (r.itemGroup || "").toLowerCase().includes(kw)
            );
        }

        // 2) 품번별 구간 집계 (전년 실적 / 당해 경영 / 당해 계획 + 전년비 / 경영비)
        const processed = filtered.map(item => {
            // 항목구분|연도|구간번호 → 값
            const bucket = new Map();
            item.rows.forEach(r => {
                const { y, i } = periodKeyOf(r, periodType, $search.monthBaseType);
                const k = `${r.iem}|${y}|${i}`;
                bucket.set(k, (bucket.get(k) || 0) + (isAmount ? r.amt : r.qy));
            });

            const valueOf = (iem, year, idx) => bucket.get(`${iem}|${year}|${idx}`) || 0;

            const rowDataMap = { prevActual: {}, mgmtPlan: {}, curPlan: {}, prevRatio: {}, mgmtRatio: {} };
            allCols.forEach(col => {
                // 각 컬럼의 전년 = 컬럼 연도 - 1 (년말확장 컬럼은 기준년도가 전년이 됨)
                rowDataMap.prevActual[col.key] = valueOf("A", col.year - 1, col.idx);
                rowDataMap.mgmtPlan[col.key] = valueOf("M", col.year, col.idx);
                rowDataMap.curPlan[col.key] = valueOf("N", col.year, col.idx);
            });

            // 계 (년말확장 컬럼은 계에 포함하지 않음)
            ["prevActual", "mgmtPlan", "curPlan"].forEach(id => {
                rowDataMap[id].total = baseCols.reduce((acc, c) => acc + rowDataMap[id][c.key], 0);
            });

            // 전년비 / 경영비
            allCols.forEach(col => {
                rowDataMap.prevRatio[col.key] = ratioOf(rowDataMap.curPlan[col.key], rowDataMap.prevActual[col.key]);
                rowDataMap.mgmtRatio[col.key] = ratioOf(rowDataMap.curPlan[col.key], rowDataMap.mgmtPlan[col.key]);
            });
            rowDataMap.prevRatio.total = ratioOf(rowDataMap.curPlan.total, rowDataMap.prevActual.total);
            rowDataMap.mgmtRatio.total = ratioOf(rowDataMap.curPlan.total, rowDataMap.mgmtPlan.total);

            return {
                itemCode: item.itemCode,
                itemName: item.itemName,
                itemGroup: item.itemGroup,
                productType: item.productType,
                integratedModel: item.integratedModel,
                model: item.model,
                planWeek: $applied.planWeek,
                rowDataMap,
            };
        });

        // 3) 0 제외 (경영, 실적, 계획이 모두 0인 품번 제외)
        let finalItems = processed;
        if ($search.excludeZero) {
            finalItems = finalItems.filter(item => {
                const m = item.rowDataMap;
                return m.prevActual.total !== 0 || m.mgmtPlan.total !== 0 || m.curPlan.total !== 0;
            });
        }

        // 3-1) 제품 컬럼 정렬 (선택된 컬럼이 숨겨져 있으면 품번 오름차순으로 대체, 동일 값은 기존 순서 유지)
        const visibleIds = $colConfig.visibleColumnIds;
        const sortKey = visibleIds.includes($sort.key) ? $sort.key : (visibleIds.includes(DEFAULT_SORT.key) ? DEFAULT_SORT.key : null);
        if (sortKey) {
            const dir = visibleIds.includes($sort.key) && $sort.dir === "desc" ? -1 : 1;
            finalItems = [...finalItems].sort((a, b) =>
                dir * SORT_COLLATOR.compare(String(a[sortKey] ?? ""), String(b[sortKey] ?? ""))
            );
        }

        // 4) 전체 [ 계 ] 합계
        const grandTotal = { prevActual: {}, mgmtPlan: {}, curPlan: {}, prevRatio: {}, mgmtRatio: {} };
        ["prevActual", "mgmtPlan", "curPlan"].forEach(id => {
            allCols.forEach(col => {
                grandTotal[id][col.key] = finalItems.reduce((acc, it) => acc + it.rowDataMap[id][col.key], 0);
            });
            grandTotal[id].total = finalItems.reduce((acc, it) => acc + it.rowDataMap[id].total, 0);
        });
        allCols.forEach(col => {
            grandTotal.prevRatio[col.key] = ratioOf(grandTotal.curPlan[col.key], grandTotal.prevActual[col.key]);
            grandTotal.mgmtRatio[col.key] = ratioOf(grandTotal.curPlan[col.key], grandTotal.mgmtPlan[col.key]);
        });
        grandTotal.prevRatio.total = ratioOf(grandTotal.curPlan.total, grandTotal.prevActual.total);
        grandTotal.mgmtRatio.total = ratioOf(grandTotal.curPlan.total, grandTotal.mgmtPlan.total);

        return {
            items: finalItems,
            grandTotal,
            periodCols: baseCols,
            extCols,
        };
    }
);

// ==========================================
// 6. 엑셀 / CSV 내보내기 함수
// ==========================================
export function exportSalesGridToCSV() {
    const grid = get(salesGridData);
    const applied = get(appliedSalesSearch);
    const search = applied;
    const config = get(salesColumnConfig);

    if (!grid || !grid.items || grid.items.length === 0) {
        toast.error("다운로드할 매출현황 데이터가 없습니다.");
        return;
    }

    const fixedCols = config.visibleColumnIds
        .map(id => MASTER_SALES_COLUMNS.find(c => c.id === id))
        .filter(Boolean);
    const fixedHeaders = fixedCols.map(c => c.label);
    const cols = grid.periodCols;
    const extCols = grid.extCols || [];
    const headers = [
        ...fixedHeaders, "편성주차", "항목",
        ...cols.map(c => c.label), "계",
        ...extCols.map(c => c.label),
    ];

    const metricDefs = buildMetricRows(applied.baseYear).filter(m => config.visibleMetricIds.includes(m.id));
    // 화면과 동일하게 소수 둘째 자리(비율은 첫째 자리)까지, 정수면 소수점 없이
    const round = (n, d = 2) => {
        const f = Math.pow(10, d);
        const r = Math.round((Number(n) || 0) * f) / f;
        return r === 0 ? 0 : r;
    };
    const cell = (val, isRatio) => isRatio ? `${round(val?.diff)} (${round(val?.ratio, 1)}%)` : round(val);
    const quote = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;

    const makeRow = (leading, week, mDef, map) => {
        const row = [...leading, quote(week), quote(mDef.label)];
        cols.forEach(c => row.push(cell(map[mDef.id]?.[c.key], mDef.isRatio)));
        row.push(cell(map[mDef.id]?.total, mDef.isRatio));
        extCols.forEach(c => row.push(cell(map[mDef.id]?.[c.key], mDef.isRatio)));
        return row.join(",");
    };

    const csvRows = [];

    // 1) 전체 합계 (계)
    metricDefs.forEach(mDef => {
        const leading = fixedHeaders.map((_, i) => quote(i === 0 ? "전체 합계" : ""));
        csvRows.push(makeRow(leading, applied.planWeek, mDef, grid.grandTotal));
    });

    // 2) 각 품목별 데이터
    grid.items.forEach(item => {
        metricDefs.forEach(mDef => {
            const leading = fixedCols.map(c => quote(item[c.id]));
            csvRows.push(makeRow(leading, item.planWeek || applied.planWeek, mDef, item.rowDataMap));
        });
    });

    const csvContent = "﻿" + [headers.map(quote).join(","), ...csvRows].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `매출현황_${search.periodType}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("매출현황 CSV 파일이 성공적으로 다운로드되었습니다.");
}

// ==========================================
// 7. 제품 분류 트리 (제품유형 > 품목그룹 > 기종) - 공급계획과 동일한 API 사용
// ==========================================
export const salesItemGroupTree = writable([]);

export async function fetchSalesItemGroupTree() {
    try {
        const res = await axios.get("bsnMngmt/itemGroupTree");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => ({
                mlsfc_code: String(item.mlsfc_code || "").trim(),
                mlsfc_nm: String(item.mlsfc_nm || "").trim(),
                item_group_code: String(item.item_group_code || "").trim(),
                item_group_nm: String(item.item_group_nm || "").trim(),
                mhrls_knd_code: String(item.mhrls_knd_code || "").trim(),
            })).filter(x => x.item_group_code);
            salesItemGroupTree.set(list);
            return list;
        }
    } catch (e) {
        console.error("제품 분류 트리 조회 오류:", e);
    }
    return [];
}
